import { vectorStore } from './vector-store-provider';

const defaultSampleData = `
# FlexHelp Property Management Knowledge Base

## Property Maintenance
Regular property maintenance is essential for preserving value and ensuring tenant satisfaction. Key maintenance tasks include:
- Checking HVAC systems quarterly
- Inspecting roofs and gutters twice yearly
- Testing smoke and carbon monoxide detectors monthly
- Scheduling professional pest control every 3-6 months
- Servicing water heaters annually

## Tenant Relations
Building positive tenant relationships helps reduce turnover and minimize disputes. Best practices include:
- Responding to maintenance requests within 24-48 hours
- Providing multiple communication channels for tenants
- Conducting move-in and move-out inspections with tenants present
- Sending renewal notices 60-90 days before lease expiration
- Offering online payment options for convenience
`;

/**
 * Initialize the RAG system with sample data if it's not already loaded
 * @returns A promise that resolves when initialization is complete
 */
export async function initializeRAGSystem(): Promise<boolean> {
  try {
    // Check if the system is already initialized
    const isReady = vectorStore.isReady();
    const documentCount = vectorStore.getDocumentCount();
    
    if (isReady && documentCount > 0) {
      console.log(`[RAG Initializer] RAG system already initialized with ${documentCount} documents`);
      return true;
    }
    
    console.log('[RAG Initializer] RAG system not initialized. Loading sample data...');
    
    // Try to load sample data
    await vectorStore.loadTrainingData(defaultSampleData);
    
    // Check again after loading
    const readyAfterLoad = vectorStore.isReady();
    const countAfterLoad = vectorStore.getDocumentCount();
    
    console.log(`[RAG Initializer] Initialization ${readyAfterLoad ? 'successful' : 'failed'}. Document count: ${countAfterLoad}`);
    
    return readyAfterLoad;
  } catch (error) {
    console.error('[RAG Initializer] Error initializing RAG system:', error);
    return false;
  }
}

/**
 * Check if there's saved training data in localStorage and load it if present
 */
export async function loadTrainingDataFromStorage(): Promise<boolean> {
  try {
    // Check if we have training data saved in localStorage
    const storedData = localStorage.getItem('flexhelp-training-data');
    
    if (storedData) {
      console.log('[RAG Initializer] Found saved training data in localStorage');
      await vectorStore.loadTrainingData(storedData);
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('[RAG Initializer] Error loading training data from storage:', error);
    return false;
  }
} 